import React, { useEffect } from "react";
import { Form, Input, Button, Checkbox } from "antd";
import { LockOutlined, MailOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "antd/dist/reset.css";
import styles from "./login.module.css";
import logo from "../../assets/img/logo5.png";
import SessionManager from "./sessionmanager";
import { auth_url } from "../data/urlData";
import { useNavigate } from "react-router-dom";

const Login = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Cookie utility functions
  const setCookie = (name, value, days = 30) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Strict;Secure`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') cookie = cookie.substring(1, cookie.length);
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
      }
    }
    return null;
  };

  const deleteCookie = (name) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  };

  // Simple encryption/decryption for password (basic obfuscation)
  const encryptPassword = (password) => {
    return btoa(password); // Base64 encoding (basic obfuscation)
  };

  const decryptPassword = (encryptedPassword) => {
    try {
      return atob(encryptedPassword); // Base64 decoding
    } catch (error) {
      return '';
    }
  };

  // Load saved credentials on component mount
  useEffect(() => {
    const savedUsername = getCookie('rememberedUsername');
    const savedPassword = getCookie('rememberedPassword');
    const rememberMe = getCookie('rememberMe') === 'true';

    if (rememberMe && savedUsername) {
      form.setFieldsValue({
        username: savedUsername,
        password: savedPassword ? decryptPassword(savedPassword) : '',
        remember: rememberMe
      });
    }
  }, [form]);

  const handleSubmit = async (values) => {
    try {
      const response = await fetch(
        `${auth_url}/realms/FOR-FREIGHT/protocol/openid-connect/token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            client_id: "API",
            username: values.username,
            password: values.password,
            grant_type: "password",
            client_secret: "",
            scope: "openid",
          }),
          redirect: "follow",
        }
      );

      if (!response.ok) throw new Error("Authentication failed");

      const result = await response.json();
      const token = result.access_token || result.token;

      if (token) {
        // Handle remember me functionality with cookies
        if (values.remember) {
          // Store credentials in cookies for persistent login
          setCookie('rememberedUsername', values.username, 30); // 30 days
          setCookie('rememberedPassword', encryptPassword(values.password), 30); // 30 days
          setCookie('rememberMe', 'true', 30); // 30 days
        } else {
          // Clear remembered credentials if not checked
          deleteCookie('rememberedUsername');
          deleteCookie('rememberedPassword');
          deleteCookie('rememberMe');
        }

        SessionManager.setUserSession(token, values.username, values.remember);
        if (SessionManager.getToken()) navigate('/home');
      } else {
        showToast("Login is unsuccessful!", "error");
      }
    } catch (error) {
      showToast("Incorrect email or password. Please try again.", "error");
      console.error("Login error:", error);
    }
  };

  const showToast = (message, type) => {
    toast[type](message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  return (
    <div className={styles.container}>
      {/* Left Side - Aron Image (Diagonal Cut) */}
      <div className={styles["left-side"]}></div>

      {/* Right Side - Login Form */}
      <div className={styles["right-side"]}>
        <div className={styles["auth-form"]}>
          <img src={logo} className={styles.logo} alt="Company Logo" />
          <h3 className={styles["form-title"]}>Welcome to FOR-FREIGHT</h3>
          <Form 
            form={form}
            name="login" 
            onFinish={handleSubmit}
          >
            <Form.Item
              name="username"
              rules={[{ required: true, message: "Please input your Username!" }]}
            >
              <Input 
                className={styles["inputlogin"]} 
                prefix={<MailOutlined />} 
                placeholder="Email" 
                autoFocus 
              />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[{ required: true, message: "Please input your Password!" }]}
            >
              <Input.Password 
                className={styles["inputlogin"]} 
                prefix={<LockOutlined />} 
                placeholder="Password" 
              />
            </Form.Item>
            
            <Form.Item>
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox className={styles["remember-checkbox"]}>
                  Remember me
                </Checkbox>
              </Form.Item>
            </Form.Item>

            <Form.Item>
              <Button htmlType="submit" className={styles["login-form-button"]}>
                Sign In
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default Login;